import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useTheme } from "../../../context/ThemeContext";
import type { ExpectedIncomeResponse } from "../services/expensesService";
import { Kpi, Notice, Spinner } from "./ui";
import type { BudgetTimeline } from "../lib/budgetTimeline";
import { CRM_FORECAST_PATH, CRM_QUOTES_PATH, INCOME_PATH } from "../lib/flowRoutes";
import { money, monthLabel } from "../lib/format";
import { projectedFlow } from "../lib/projectedFlow";
import { ui } from "../theme";

// Presupuestos → Flujo presupuestado (#1290): el ingreso esperado del CRM
// (contratos fijos, por cobrar y pipeline ponderado) contra el gasto
// presupuestado de cada mes, con el neto acumulado de lo que falta del año.
// Las cotizaciones enviadas sin vencer (#1546) son una fuente aparte, como el
// pipeline: no cuentan como ingreso en firme.
// Se calcula en vivo: cambiar un negocio, su cobro o una línea de presupuesto
// mueve estos totales.

export const FLOW_COLORS = { actual: "#2a78d6", contracts: "#0d9488", receivable: "#0891b2", pipeline: "#d97706", quotes: "#c026d3", spend: "#6366f1", cumulative: "#111827" };

const kFormat = (v: unknown) => {
  const n = Number(v);
  return Math.abs(n) >= 1000 ? `$${Math.round(n / 1000)}k` : `$${Math.round(n)}`;
};

/**
 * El ingreso esperado lo pide la página con el periodo del filtro (#1332) y lo
 * comparte con el resumen ejecutivo: aquí solo se cruza con el gasto y se pinta.
 */
export default function ProjectedFlowCard({ timeline, data, error }: { timeline: BudgetTimeline; data: ExpectedIncomeResponse | null; error?: string | null }) {
  const { t, i18n } = useTranslation(["common", "expensesStatements"]);
  const tf = (k: string, o?: Record<string, unknown>) => t(`expensesStatements:flow.${k}`, o);
  const language = i18n.language;
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);

  const flow = useMemo(() => (data ? projectedFlow(data.months, timeline.months) : null), [data, timeline]);
  const fmt = (v: unknown) => (v == null ? "—" : money(Number(v), "MXN", language));
  const spansYears = timeline.from.slice(0, 4) !== timeline.to.slice(0, 4);
  const short = (p: string) => (spansYears ? `${monthLabel(p, language).split(" ")[0]} ${p.slice(2, 4)}` : monthLabel(p, language).split(" ")[0]);

  const axis = { fontSize: 11, fill: isDarkMode ? "#9ca3af" : "#6b7280" };
  const grid = isDarkMode ? "#333" : "#e5e7eb";
  const tooltipStyle = { fontSize: 12, backgroundColor: isDarkMode ? "#1f1f1f" : "#fff", borderColor: grid, color: isDarkMode ? "#fff" : "#111" };

  const chart = (flow?.months || []).map((m) => ({
    label: short(m.period),
    actual: m.actual || null,
    contracts: m.contracts || null,
    receivable: m.receivable || null,
    pipeline: m.pipeline || null,
    quotes: m.quotes || null,
    spend: m.spend || null,
    cumulative: m.cumulative,
  }));
  const r = flow?.remaining;
  const coverage = r && r.spend > 0 ? Math.round((r.firm / r.spend) * 100) : null;
  const hasQuotes = Boolean(flow && (flow.months.some((m) => m.quotes > 0) || (data?.quotes || []).length > 0));

  return (
    <section aria-label={tf("title")} className={`${c.card} space-y-4`} data-testid="projected-flow">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className={`text-sm font-semibold ${c.text}`}>
            <i className="ri-exchange-funds-line mr-1 text-teal-600" />
            {tf("title")}
          </h2>
          <p className={`text-xs ${c.muted}`}>{tf("subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-3 text-xs">
          <a href={INCOME_PATH} className="text-teal-600 hover:underline">
            <i className="ri-arrow-down-circle-line mr-1" />
            {tf("links.income")}
          </a>
          <a href={CRM_FORECAST_PATH} className="text-teal-600 hover:underline">
            <i className="ri-line-chart-line mr-1" />
            {tf("links.forecast")}
          </a>
          <a href={CRM_QUOTES_PATH} className="text-teal-600 hover:underline">
            <i className="ri-file-text-line mr-1" />
            {tf("links.quotes")}
          </a>
        </div>
      </div>

      {error && <Notice tone="warn">{error === "load" ? tf("loadError") : error}</Notice>}
      {!data && !error && <Spinner />}

      {data && flow && r && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi
              label={tf("kpi.income")}
              value={fmt(r.income)}
              hint={r.quotes > 0 ? tf("kpi.incomeHintQuotes", { firm: fmt(r.firm), pipeline: fmt(r.pipeline), quotes: fmt(r.quotes) }) : tf("kpi.incomeHint", { firm: fmt(r.firm), pipeline: fmt(r.pipeline) })}
            />
            <Kpi label={tf("kpi.spend")} value={fmt(r.spend)} hint={tf(r.usesRunRate ? "kpi.spendHintRunRate" : "kpi.spendHint", { count: r.months })} />
            <Kpi label={tf("kpi.net")} value={fmt(r.net)} tone={r.net < 0 ? "bad" : "good"} hint={tf("kpi.netHint", { amount: fmt(r.netWithoutPipeline) })} />
            <Kpi label={tf("kpi.fixed")} value={fmt(data.monthlyFixed)} hint={coverage == null ? tf("kpi.fixedHintNone", { count: data.contracts.length }) : tf("kpi.fixedHint", { count: data.contracts.length, pct: coverage })} />
          </div>

          {flow.firstNegative && <Notice tone="warn">{tf("negative", { month: monthLabel(flow.firstNegative, language) })}</Notice>}
          {data.contracts.length === 0 && data.totals.expected === 0 && <Notice>{tf("empty")}</Notice>}
          {data.undated.count > 0 && <p className={`text-xs ${c.muted}`}>{tf("undated", { count: data.undated.count, amount: fmt(data.undated.weighted) })}</p>}
          {data.withoutRate.length > 0 && <Notice tone="warn">{tf("withoutRate", { currencies: data.withoutRate.join(", ") })}</Notice>}

          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                <XAxis dataKey="label" tick={axis} />
                <YAxis tick={axis} tickFormatter={kFormat} width={52} />
                <Tooltip formatter={(v: number, name: string) => [fmt(v), name]} contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={0} stroke={grid} />
                <Bar stackId="income" dataKey="actual" name={tf("series.actual")} fill={FLOW_COLORS.actual} isAnimationActive={false} />
                <Bar stackId="income" dataKey="contracts" name={tf("series.contracts")} fill={FLOW_COLORS.contracts} isAnimationActive={false} />
                <Bar stackId="income" dataKey="receivable" name={tf("series.receivable")} fill={FLOW_COLORS.receivable} isAnimationActive={false} />
                <Bar stackId="income" dataKey="pipeline" name={tf("series.pipeline")} fill={FLOW_COLORS.pipeline} fillOpacity={0.7} radius={hasQuotes ? undefined : [4, 4, 0, 0]} isAnimationActive={false} />
                {hasQuotes && <Bar stackId="income" dataKey="quotes" name={tf("series.quotes")} fill={FLOW_COLORS.quotes} fillOpacity={0.7} radius={[4, 4, 0, 0]} isAnimationActive={false} />}
                <Bar dataKey="spend" name={tf("series.spend")} fill={FLOW_COLORS.spend} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                <Line dataKey="cumulative" name={tf("series.cumulative")} stroke={isDarkMode ? "#e5e7eb" : FLOW_COLORS.cumulative} strokeWidth={2} dot={{ r: 2 }} connectNulls={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className={`text-left text-xs ${c.muted}`}>
                  <th className="px-2 py-2">{tf("table.month")}</th>
                  <th className="px-2 py-2 text-right">{tf("series.actual")}</th>
                  <th className="px-2 py-2 text-right">{tf("series.contracts")}</th>
                  <th className="px-2 py-2 text-right">{tf("series.receivable")}</th>
                  <th className="px-2 py-2 text-right">{tf("series.pipeline")}</th>
                  {hasQuotes && <th className="px-2 py-2 text-right">{tf("series.quotes")}</th>}
                  <th className="px-2 py-2 text-right">{tf("series.spend")}</th>
                  <th className="px-2 py-2 text-right">{tf("table.net")}</th>
                  <th className="px-2 py-2 text-right">{tf("series.cumulative")}</th>
                </tr>
              </thead>
              <tbody>
                {flow.months.map((m) => (
                  <tr key={m.period} className={`border-t ${c.border} ${c.text} ${m.phase === "past" ? "opacity-60" : ""}`} data-testid={`flow-${m.period}`}>
                    <td className="whitespace-nowrap px-2 py-1.5">
                      {monthLabel(m.period, language)}
                      {m.phase === "current" && <span className="ml-2 rounded-full bg-teal-500/15 px-2 py-0.5 text-[11px] text-teal-600">{tf("table.current")}</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{m.actual ? fmt(m.actual) : "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{m.contracts ? fmt(m.contracts) : "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{m.receivable ? fmt(m.receivable) : "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{m.pipeline ? fmt(m.pipeline) : "—"}</td>
                    {hasQuotes && <td className="px-2 py-1.5 text-right tabular-nums">{m.quotes ? fmt(m.quotes) : "—"}</td>}
                    <td className="px-2 py-1.5 text-right tabular-nums" title={tf(`source.${m.spendSource}`)}>
                      {fmt(m.spend)}
                      {m.spendSource === "runRate" && m.spend > 0 && <span className={`ml-1 text-[11px] ${c.muted}`}>*</span>}
                    </td>
                    <td className={`px-2 py-1.5 text-right font-medium tabular-nums ${m.net < 0 ? "text-red-500" : ""}`}>{fmt(m.net)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${(m.cumulative ?? 0) < 0 ? "text-red-500" : ""}`}>{m.cumulative == null ? "—" : fmt(m.cumulative)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.usesRunRate && <p className={`text-xs ${c.muted}`}>* {tf("source.runRate")}</p>}
        </>
      )}
    </section>
  );
}
